import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subject, Subscription, filter, merge, take, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import {
	selectCurrentDevice,
	selectCurrentDeviceStatus,
	selectMtmaFwAutoUpdateTimeslot,
} from 'src/app/store/device/device.selector';
import { selectUser } from 'src/app/store/user/user.selector';
import { DeviceStatus } from 'src/app/model/device-status';
import { CLOUD_FW_UPGRADE_STATE } from 'src/app/conf/firmware-upgrade';
import { StandardDesignParameters, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { MtmaFwAutoUpdateTimeslot } from 'src/app/model/mtma-fw-autoupdate-timeslot';
import { WeekDay } from 'src/app/model/week-day';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { Device } from 'src/app/model/device';
import { User } from 'src/app/model/user';

@Component({
	templateUrl: './update-firmware-timeslots.dialog.component.html',
})
export class UpdateFirmwareTimeslotsDialogComponent implements OnInit, OnDestroy {
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	currentDevice$: Observable<Device | undefined>;
	currentDeviceSub: Subscription | undefined;
	user$: Observable<User | undefined>;
	userSub: Subscription | undefined;
	mtmaFwAutoUpdateTimeslot$: Observable<MtmaFwAutoUpdateTimeslot | undefined>;
	timeslotSub: Subscription | undefined;

	deviceStatus: DeviceStatus;
	form: FormGroup;

	daysOfWeek: WeekDay[] = [
		{ Value: '0', Name: 'SUN' },
		{ Value: '1', Name: 'MON' },
		{ Value: '2', Name: 'TUE' },
		{ Value: '3', Name: 'WED' },
		{ Value: '4', Name: 'THU' },
		{ Value: '5', Name: 'FRI' },
		{ Value: '6', Name: 'SAT' },
	];

	private deviceId: number | undefined;
	private userId: number | undefined;
	private initialFwTimeslotsFetchDispatched = false;
	private readonly destroy$ = new Subject<void>();
	private confirmOutcomeSub: Subscription | undefined;

	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private dialogRef: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private actions$: Actions,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.currentDevice$ = this.store.select(selectCurrentDevice);
		this.user$ = this.store.select(selectUser);
		this.mtmaFwAutoUpdateTimeslot$ = this.store.select(selectMtmaFwAutoUpdateTimeslot);
	}

	ngOnInit(): void {
		StandardNewValidator.setFirstCompile(true);
		this.buildForm();
		this.subscribeAll();
		this.onResize();
	}

	private buildForm(): void {
		const weekControls = this.daysOfWeek.map(() => this.fb.control(false));
		this.form = this.fb.group({
			autoUpdate: [false],
			schedule: this.fb.group({
				weekDays: this.fb.array(weekControls, CustomValidators.multipleCheckboxRequireOne),
				startTime: [this.hhmmToDate('02:00'), Validators.required],
				endTime: [this.hhmmToDate('03:00'), Validators.required],
			}),
		});
		this.schedule.addValidators(CustomValidators.checkEndTimeAtLeastMinutesAfterStart('startTime', 'endTime', 60));

		// Enable/disable the schedule when the auto update is enabled/disabled
		this.form.get('autoUpdate')!.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((on: boolean) => {
			if (on) {
				this.schedule.enable({ emitEvent: false });
			} else {
				this.schedule.disable({ emitEvent: false });
			}
		});
		// Disable the schedule by default
		this.schedule.disable({ emitEvent: false });
	}

	get schedule(): FormGroup {
		return this.form.get('schedule') as FormGroup;
	}

	get f(): any {
		return this.form.controls;
	}

	// Schedule nested controls
	get sf(): any {
		return this.schedule.controls;
	}

	subscribeAll(): void {
		this.deviceStatusSub = this.deviceStatus$
			.pipe(
				filter((d): d is DeviceStatus => !!d),
				takeUntil(this.destroy$),
			).subscribe((s) => {
			this.deviceStatus = s;
		});
		this.currentDeviceSub = this.currentDevice$
			.pipe(
				filter((d): d is Device => !!d?.Id),
				takeUntil(this.destroy$),
			)
			.subscribe((device) => {
				this.deviceId = device.Id;
				this.tryDispatchFwTimeslotsGet();
			});
		this.userSub = this.user$
			.pipe(
				filter((u): u is User => u != null && u.Id != null),
				takeUntil(this.destroy$),
			)
			.subscribe((user) => {
				this.userId = user.Id as number;
				this.tryDispatchFwTimeslotsGet();
			});
		this.timeslotSub = this.mtmaFwAutoUpdateTimeslot$.pipe(takeUntil(this.destroy$)).subscribe((slot) => {
			this.patchFromTimeslot(slot);
		});
	}

	/**
	 * Try to dispatch the firmware timeslots get action.
	 * If the action is already dispatched, do nothing.
	 * If the device ID or user ID is not set, do nothing.
	 * Otherwise, dispatch the action and set the initial firmware timeslots fetch dispatched flag to true.
	 */
	private tryDispatchFwTimeslotsGet(): void {
		if (this.initialFwTimeslotsFetchDispatched) {
			return;
		}
		if (this.deviceId == null || this.userId == null) {
			return;
		}
		this.initialFwTimeslotsFetchDispatched = true;
		this.store.dispatch(
			DeviceActions.getMtmaFwAutoUpdateTimeslots({ deviceId: this.deviceId, userId: this.userId }),
		);
	}

	private patchFromTimeslot(slot: MtmaFwAutoUpdateTimeslot | undefined | null): void {
		const weekDays = this.weekDays;
		if (!slot) {
			this.form.patchValue({ autoUpdate: false }, { emitEvent: false });
			this.resetWeekDaysMask(8); // 127 = 0b1111111 = all days of the week, 8 = default only wednesday
			this.schedule.patchValue(
				{
					startTime: this.hhmmToDate('02:00'),
					endTime: this.hhmmToDate('03:00'),
				},
				{ emitEvent: false },
			);
			this.schedule.disable({ emitEvent: false });
			weekDays.updateValueAndValidity({ emitEvent: false });
			return;
		}
		this.resetWeekDaysMask(slot.Weekdays === 0 ? 127 : slot.Weekdays); // 127 = 0b1111111 = all days of the week
		this.schedule.patchValue(
			{
				startTime: this.hhmmToDate(slot.StartHour),
				endTime: this.hhmmToDate(slot.EndHour),
			},
			{ emitEvent: false },
		);
		this.form.patchValue({ autoUpdate: true }, { emitEvent: false });
		this.schedule.enable({ emitEvent: false });
		weekDays.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Reset the week days mask to the given mask
	 * @param mask - The mask to reset the week days to
	 */
	private resetWeekDaysMask(mask: number): void {
		const weekDays = this.weekDays;
		const selected: boolean[] = new Array(7).fill(false);
		let remaining = mask;
		for (let i = 6; i > -1; i--) {
			if (remaining !== 0 && remaining / 2 ** i >= 1) {
				selected[i] = true;
			}
			remaining = remaining % 2 ** i;
		}
		weekDays.controls.forEach((c, idx) => {
			c.setValue(selected[idx], { emitEvent: false });
		});
	}

	/**
	 * Encode the week days mask to a number
	 * @returns The encoded week days mask
	 */
	private encodeWeekdayMask(): number {
		const weekDays = this.weekDays;
		let v = 0;
		weekDays.controls.forEach((c, i) => {
			if (c.value) {
				v += 2 ** i;
			}
		});
		return v;
	}

	private formatTimeControl(val: unknown): string {
		if (val instanceof Date) {
			return moment(val).format('HH:mm');
		}
		if (typeof val === 'string') {
			const m = moment(val, 'HH:mm', true);
			return m.isValid() ? m.format('HH:mm') : '00:00';
		}
		return '00:00';
	}

	private hhmmToDate(hhmm: string): Date {
		const m = moment(hhmm, 'HH:mm', true);
		if (!m.isValid()) {
			return moment().startOf('day').toDate();
		}
		return moment().startOf('day').hours(m.hours()).minutes(m.minutes()).seconds(0).milliseconds(0).toDate();
	}

	confirm(): void {
		// Check if the device ID or user ID is set
		if (this.deviceId == null || this.userId == null) {
			return;
		}
		// If pristine, simply close the dialog
		if (this.form.pristine) {
			this.dialogRef.close(true);
			return;
		}
		// Subscribe to the outcome of the firmware timeslots update/delete actions:
		// - Success: close the dialog
		// - Failure: do not close the dialog
		this.confirmOutcomeSub?.unsubscribe();
		this.confirmOutcomeSub = merge(
			this.actions$.pipe(
				ofType(
					DeviceActions.postMtmaFwAutoUpdateTimeslotsSuccess,
					DeviceActions.deleteMtmaFwAutoUpdateTimeslotsSuccess,
				),
				map(() => true),
			),
			this.actions$.pipe(
				ofType(
					DeviceActions.postMtmaFwAutoUpdateTimeslotsFailed,
					DeviceActions.deleteMtmaFwAutoUpdateTimeslotsFailed,
				),
				map(() => false),
			),
		)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe((ok) => {
				if (ok) {
					// Close the dialog with success
					this.dialogRef.close(true);
				}
			});

		const enabled = !!this.form.get('autoUpdate')?.value;
		if (!enabled) {
			// The autoupdate is disabled: dispatch the firmware timeslots delete action
			this.store.dispatch(
				DeviceActions.deleteMtmaFwAutoUpdateTimeslots({ deviceId: this.deviceId, userId: this.userId }),
			);
			return;
		}
		else {
			StandardNewValidator.setFirstCompile(false);
			// The autoupdate is enabled: mark all the schedule controls as touched
			this.schedule.markAllAsTouched();
			// Update the schedule validity
			this.schedule.updateValueAndValidity({ onlySelf: false, emitEvent: false });
			if (!this.schedule.valid) {
				// The schedule is not valid: do not close the dialog
				this.confirmOutcomeSub?.unsubscribe();
				return;
			}
			// Encode the week days mask
			const mask = this.encodeWeekdayMask();
			// If the mask is 0, do not close the dialog
			if (mask === 0) {
				this.confirmOutcomeSub?.unsubscribe();
				return;
			}
			// Dispatch the firmware timeslots update action
			this.store.dispatch(
				DeviceActions.postMtmaFwAutoUpdateTimeslots({
					deviceId: this.deviceId,
					userId: this.userId,
					body: {
						Weekdays: mask,
						StartHour: this.formatTimeControl(this.schedule.get('startTime')?.value),
						EndHour: this.formatTimeControl(this.schedule.get('endTime')?.value),
					},
				}),
			);
		}
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		StandardNewValidator.setFirstCompile(false);
		this.destroy$.next();
		this.destroy$.complete();
		this.deviceStatusSub?.unsubscribe();
		this.currentDeviceSub?.unsubscribe();
		this.userSub?.unsubscribe();
		this.timeslotSub?.unsubscribe();
		this.confirmOutcomeSub?.unsubscribe();
	}

	get weekDays(): FormArray {
		return this.schedule.get('weekDays') as FormArray;
	}

	offline(): boolean {
		return !this.deviceStatus?.Online;
	}

	onFirmwareUpdate(): boolean {
		return (
			this.deviceStatus?.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_STARTED ||
			this.deviceStatus?.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_UPDATING
		);
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth,'w-20rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	customPaddingX() : string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	manageRespStylewithError(type: any, classType: string): string {
		return StandardNewValidator.manageRespStylewithError(type, classType, this.screenWidth);
	}

	newShowErrors(type: any, classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredCheckBox(type: any): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}

	getSpaceBottomErrorMessage(type: any): string {
		return (this.screenWidth > StandardDesignParameters.MAX_MD_WIDTH) && !this.showErrorRequiredMessage(type)
			? ' pb-5'
			: '';
	}

	showErrorRequiredMessage(type: any): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}
}
